/**
 * Draggable SVG tonearm component for the turntable.
 * Renders an AT-LP120-style S-curved arm with counterweight, headshell,
 * and stylus needle that lifts/lowers. Includes a vertical volume fader.
 * During drag, a tooltip shows the target track and timestamp.
 */
import { useState, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { 
  TONEARM_START_ANGLE, 
  TONEARM_END_ANGLE, 
  TONEARM_REST_ANGLE,
  TONEARM_RANGE,
  type VinylTrack,
} from "@/hooks/useVinylPlayer";

// Format seconds to mm:ss
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Calculate track and time from display angle (CSS rotation)
// Display angle is negated from hook angle: displayAngle = -hookAngle
// At album start: hookAngle = 8, displayAngle = -8
// At album end: hookAngle = -13, displayAngle = 13
const getTrackInfoFromAngle = (
  displayAngle: number, 
  tracks: VinylTrack[]
): { trackIndex: number; timeInTrack: number; trackTitle: string } | null => {
  if (!tracks.length) return null;
  
  // Convert display angle to hook angle
  const hookAngle = -displayAngle;
  
  // Check if within valid range (hook coordinates)
  if (hookAngle > TONEARM_START_ANGLE || hookAngle < TONEARM_END_ANGLE) {
    return null;
  }
  
  const totalDuration = tracks.reduce((sum, t) => sum + t.duration, 0);
  // Progress: 0 at start (hookAngle=8), 1 at end (hookAngle=-13)
  const overallProgress = (TONEARM_START_ANGLE - hookAngle) / TONEARM_RANGE;
  const targetTime = Math.max(0, Math.min(totalDuration, overallProgress * totalDuration));
  
  // Find which track
  let cumulativeTime = 0;
  for (let i = 0; i < tracks.length; i++) {
    if (cumulativeTime + tracks[i].duration > targetTime) {
      return {
        trackIndex: i,
        timeInTrack: targetTime - cumulativeTime,
        trackTitle: tracks[i].title,
      };
    }
    cumulativeTime += tracks[i].duration;
  }
  
  // End of album
  const lastTrack = tracks[tracks.length - 1];
  return {
    trackIndex: tracks.length - 1,
    timeInTrack: lastTrack.duration,
    trackTitle: lastTrack.title,
  };
};

interface TonearmProps {
  angle: number;
  isLifted: boolean;
  isActive: boolean;
  volume: number;
  tracks?: VinylTrack[];
  onVolumeChange?: (volume: number) => void;
  onDragStart?: () => void;
  onDragEnd?: (angle: number) => void;
  onDrag?: (angle: number) => void;
  onClick?: () => void;
}

const Tonearm = ({ 
  angle, 
  isLifted, 
  isActive, 
  volume, 
  tracks = [],
  onVolumeChange,
  onDragStart,
  onDragEnd,
  onDrag,
  onClick,
}: TonearmProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragAngle, setDragAngle] = useState(0);
  const armRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const didDragRef = useRef(false);
  const pointerDownTimeRef = useRef(0);
  
  const displayAngle = isDragging 
    ? dragAngle 
    : (isActive ? (angle === TONEARM_REST_ANGLE ? TONEARM_REST_ANGLE : -angle) : TONEARM_REST_ANGLE);
  
  const topPercent = 23;
  const rightPercent = 6.5;

  // Handle drag on the tonearm
  const handleDragStart = () => {
    didDragRef.current = false;
    pointerDownTimeRef.current = Date.now();
    setIsDragging(true);
    setDragAngle(displayAngle);
    onDragStart?.();
  };

  const handlePointerUp = () => {
    const elapsed = Date.now() - pointerDownTimeRef.current;
    if (!didDragRef.current && elapsed < 200) {
      onClick?.();
    }
  };

  const handleDrag = (e: MouseEvent | TouchEvent | PointerEvent, info: { delta: { x: number; y: number } }) => {
    if (!containerRef.current) return;
    
    // Mark as actual drag if moved more than trivial amount
    if (Math.abs(info.delta.x) > 1 || Math.abs(info.delta.y) > 1) {
      didDragRef.current = true;
    }
    
    // Convert horizontal drag to rotation (display coordinates)
    const sensitivity = 0.3;
    const newAngle = dragAngle - (info.delta.x * sensitivity);
    
    // Clamp to allowed range in display coordinates
    const minDisplayAngle = -TONEARM_START_ANGLE - 5;
    const maxDisplayAngle = Math.max(-TONEARM_END_ANGLE + 5, TONEARM_REST_ANGLE + 5);
    const clampedAngle = Math.max(minDisplayAngle, Math.min(maxDisplayAngle, newAngle));
    
    setDragAngle(clampedAngle);
    onDrag?.(clampedAngle);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    if (didDragRef.current) {
      onDragEnd?.(dragAngle);
    } else {
      // No real drag movement — treat as a click
      onClick?.();
    }
  };
  
  return (
    <div ref={containerRef} className="absolute inset-0 pointer-events-none z-20">
      {/* Tonearm assembly area - right side of turntable */}
      
      {/* Anti-skate adjustment knob - small dial */}
      <div className="absolute top-[12%] right-[8%] w-[6%] aspect-square rounded-full bg-gradient-to-br from-[hsl(0,0%,25%)] to-[hsl(0,0%,15%)] shadow-lg">
        <div className="absolute inset-[20%] rounded-full bg-[hsl(0,0%,18%)]" />
        <div className="absolute inset-[35%] rounded-full border border-[rgba(255,255,255,0.08)]" />
      </div>

      {/* Tonearm pivot base - large circular housing */}
      <div className="absolute top-[22%] right-[4%] w-[14%] aspect-square rounded-full bg-gradient-to-br from-[hsl(0,0%,20%)] to-[hsl(0,0%,10%)] shadow-xl">
        {/* Inner recess */}
        <div className="absolute inset-[12%] rounded-full bg-[hsl(0,0%,8%)] shadow-inner">
          {/* Pivot center post */}
          <div className="absolute inset-[35%] rounded-full bg-gradient-to-br from-[hsl(0,0%,25%)] to-[hsl(0,0%,15%)]" />
        </div>
      </div>


      {/* The S-curved tonearm - pivots from the base - DRAGGABLE */}
      <motion.div
        ref={armRef}
        className="absolute pointer-events-auto cursor-grab active:cursor-grabbing"
        style={{
          top: `${topPercent}%`,
          right: `${rightPercent}%`,
          width: '35%',
          height: '55%',
          transformOrigin: '82% 12%',
        }}
        animate={{ rotate: displayAngle }}
        transition={isDragging ? { duration: 0 } : { duration: 0.9, ease: [0.4, 0, 0.2, 1] }}
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0}
        dragMomentum={false}
        onDragStart={handleDragStart}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        onPointerUp={handlePointerUp}
        whileHover={{ scale: 1.02 }}
      >
        {/* SVG S-curved tonearm - proper AT-LP120 style */}
        <svg 
          viewBox="0 0 120 140" 
          className="w-full h-full"
          style={{ overflow: 'visible' }}
        >
          <defs>
            <linearGradient id="armTubeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="hsl(0,0%,30%)" />
              <stop offset="40%" stopColor="hsl(0,0%,20%)" />
              <stop offset="60%" stopColor="hsl(0,0%,25%)" />
              <stop offset="100%" stopColor="hsl(0,0%,18%)" />
            </linearGradient>
            <linearGradient id="armHighlight" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.2)" />
              <stop offset="50%" stopColor="rgba(255,255,255,0.05)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0.15)" />
            </linearGradient>
          </defs>

          {/* Counterweight - cylindrical weight behind pivot */}
          <ellipse cx="105" cy="15" rx="10" ry="8" fill="hsl(0,0%,22%)" />
          <ellipse cx="105" cy="15" rx="8" ry="6" fill="hsl(0,0%,18%)" />
          <ellipse cx="105" cy="15" rx="5" ry="4" fill="hsl(0,0%,14%)" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />

          {/* Main S-curved arm tube */}
          <path
            d="M 98 18 
               C 88 20, 80 30, 70 45
               C 58 65, 45 85, 25 100
               L 15 110"
            fill="none"
            stroke="url(#armTubeGradient)"
            strokeWidth="5"
            strokeLinecap="round"
          />
          
          {/* Arm tube highlight (reflection) */}
          <path
            d="M 98 18 
               C 88 20, 80 30, 70 45
               C 58 65, 45 85, 25 100
               L 15 110"
            fill="none"
            stroke="url(#armHighlight)"
            strokeWidth="2"
            strokeLinecap="round"
          />

          {/* Headshell - fixed properly to arm end */}
          <g transform="translate(15, 110) rotate(-40)">
            {/* Headshell mount connector */}
            <rect x="-8" y="-3" width="16" height="5" rx="1" fill="hsl(0,0%,25%)" />
            
            {/* Headshell body */}
            <rect x="-22" y="-4" width="16" height="7" rx="1.5" fill="hsl(0,0%,22%)" />
            {/* Headshell top highlight */}
            <rect x="-22" y="-4" width="16" height="2" rx="1" fill="rgba(255,255,255,0.08)" />

            {/* Cartridge body */}
            <rect x="-28" y="-3" width="8" height="5" rx="0.5" fill="hsl(0,0%,15%)" />

            {/* Stylus/Needle assembly */}
            <motion.g
              animate={{ y: isLifted || isDragging ? -3 : 0 }}
              transition={{ duration: 0.25 }}
            >
              {/* Cantilever */}
              <line x1="-26" y1="2" x2="-26" y2="9" stroke="hsl(0,0%,45%)" strokeWidth="0.8" />
              {/* Needle tip */}
              <circle cx="-26" cy="10" r="1" fill="hsl(0,0%,55%)" />
            </motion.g>
          </g>
        </svg>
        
        {/* Drag hint indicator - shows track and time - positioned at needle tip */}
        {isDragging && tracks.length > 0 && (() => {
          const info = getTrackInfoFromAngle(dragAngle, tracks);
          // Counter-rotate to keep label upright
          const counterRotate = -displayAngle;
          if (!info) {
            return (
            <div 
              className="absolute bottom-[15%] left-[5%] pointer-events-none z-[9999]"
              style={{ 
                transform: `rotate(${counterRotate}deg) translate(-30px, -30px)` 
              }}
            >
                <div className="relative bg-destructive/90 text-destructive-foreground text-xs px-2.5 py-1.5 rounded font-body whitespace-nowrap shadow-lg">
                  Outside vinyl
                  {/* Speech bubble triangle */}
                  <div className="absolute -bottom-1 left-3 w-2 h-2 bg-destructive/90 rotate-45" />
                </div>
              </div>
            );
          }
          return (
            <div 
              className="absolute bottom-[15%] left-[5%] pointer-events-none z-[9999]"
              style={{ 
                transform: `rotate(${counterRotate}deg) translate(-30px, -30px)` 
              }}
            >
              <div className="relative bg-foreground/90 text-background text-xs px-2.5 py-1.5 rounded font-body whitespace-nowrap shadow-lg">
                {formatTime(info.timeInTrack)} • {info.trackTitle}
                {/* Speech bubble triangle */}
                <div className="absolute -bottom-1 left-3 w-2 h-2 bg-foreground/90 rotate-45" />
              </div>
            </div>
          );
        })()}
      </motion.div>

      {/* Volume slider - vertical fader on right side */}
      <div className="absolute bottom-[10%] right-[4%] w-[8%] h-[28%] rounded bg-[hsl(0,0%,6%)] shadow-inner">
        {/* Slider track */}
        <div className="absolute left-[15%] right-[15%] top-[8%] bottom-[8%] mx-auto w-[2px] bg-[hsl(0,0%,20%)] rounded-full" />
        {/* Volume fill indicator */}
        <div 
          className="absolute left-[15%] right-[15%] bottom-[8%] mx-auto w-[2px] bg-foreground/40 rounded-full"
          style={{ height: `${volume * 84}%` }}
        />
        {/* Slider handle - interactive */}
        <motion.div
          className="absolute left-[15%] right-[15%] h-[10%] bg-gradient-to-b from-[hsl(0,0%,35%)] to-[hsl(0,0%,22%)] rounded shadow-md cursor-pointer pointer-events-auto"
          style={{ bottom: `${8 + volume * 74}%` }}
          drag="y"
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={0}
          onDrag={(_, info) => {
            const sliderHeight = 100;
            const newVolume = Math.max(0, Math.min(1, volume - (info.delta.y / sliderHeight)));
            onVolumeChange?.(newVolume);
          }}
          whileHover={{ scale: 1.1 }}
        />
      </div>

      {/* "VOL" label near volume slider */}
      <div className="absolute bottom-[4%] right-[4%] text-[5px] text-muted-foreground/50 uppercase tracking-wider">
        Vol
      </div>
    </div>
  );
};

export default Tonearm;
